/** The path to filing: five stations in order, the current one larger, the first blocker named -
 * ADR-0212 decision 6 and the build plan's section 8.
 *
 * **This is the skeleton.** The stations are the filing's real ones in dependency order: Data in,
 * Figures verified, Approvals, Disclosures complete, Issued. The package that owns Filings builds the
 * steps from the checks read (`screens/filingSpine.ts`) and passes them in; this component draws them
 * (`StatusPath`), names the first blocker in three to six words with its fix link, and carries the
 * explanation of how the path is drawn behind an info control. It decides nothing: a station is done
 * only when the server says so, a check nobody ran is "not checked", and the path is **not** an
 * approval and not a statement that a report is fit to file.
 *
 * The blocker is checked in development (3 to 6 words), as a tile's label is, so a sentence cannot be
 * put where a short name goes.
 */

import type { ReactNode } from "react";

import type { SpineStep } from "../../screens/filingSpine";
import { InfoPopover } from "./InfoPopover";
import styles from "./PathToFiling.module.css";
import { StatusPath } from "./StatusPath";
import { labelWords } from "./Tile";

export const BLOCKER_MIN_WORDS = 3;
export const BLOCKER_MAX_WORDS = 6;

export interface Blocker {
  /** Three to six words: "Three sites have no January bill". */
  text: string;
  /** The way to fix it, for a role that can take it; built by the caller (a router `Link`). */
  link?: ReactNode;
}

export function PathToFiling({
  steps,
  blocker,
  label = "Path to filing",
}: {
  steps: readonly SpineStep[];
  blocker?: Blocker;
  label?: string;
}) {
  if (import.meta.env.DEV && blocker) {
    const words = labelWords(blocker.text);
    if (words < BLOCKER_MIN_WORDS || words > BLOCKER_MAX_WORDS) {
      throw new Error(
        `The blocker "${blocker.text}" is ${String(words)} words; name it in ${String(BLOCKER_MIN_WORDS)} to ${String(BLOCKER_MAX_WORDS)} (ADR-0212).`,
      );
    }
  }
  return (
    <section className={styles.path} aria-label={label} data-testid="path-to-filing">
      <StatusPath steps={steps} label={label} emphasis="current" />
      <div className={styles.foot}>
        {blocker ? (
          <p className={styles.blocker} data-testid="path-blocker">
            <span className={styles.word}>Next:</span> {blocker.text}
            {blocker.link ? <span className={styles.link}>{blocker.link}</span> : null}
          </p>
        ) : (
          <span />
        )}
        <InfoPopover infoKey="shell.path-to-filing" />
      </div>
    </section>
  );
}
